import { Activity, Bandage, ClipboardList, ClipboardPlus, Dumbbell, FileBarChart, FileSpreadsheet, Flag, HandHeart, HeartPulse, Plus, Stethoscope } from 'lucide-react';
import { lazyComponent } from '@/platform/lazyComponent';
import type { PlatformModule } from '@/platform/modules';
import { physioAthleteRoutes, physioRoutes } from './routes';

const AthleteClinicalCards = lazyComponent(() => import('./athlete/AthleteClinicalCards').then((module) => module.AthleteClinicalCards));
const AthleteInjuryHistory = lazyComponent(() => import('./athlete/AthleteClinicalCards').then((module) => module.AthleteInjuryHistory));
const AthleteClinicalNotice = lazyComponent(() => import('./athlete/AthleteClinicalNotice').then((module) => module.AthleteClinicalNotice));
const AthleteListInjuryColumn = lazyComponent(() => import('./athlete/AthleteListInjuryColumn').then((module) => module.AthleteListInjuryColumn));
const RecordInjuryAction = lazyComponent(() => import('./athlete/RecordInjuryAction').then((module) => module.RecordInjuryAction));
const PhysioDashboard = lazyComponent(() => import('./PhysioDashboard').then((module) => module.PhysioDashboard));
const ClinicalCaseReport = lazyComponent(() => import('./reports/ClinicalCaseReport').then((module) => module.ClinicalCaseReport));
const InjuryHistoryReport = lazyComponent(() => import('./reports/InjuryHistoryReport').then((module) => module.InjuryHistoryReport));
const InjurySummaryReport = lazyComponent(() => import('./reports/InjurySummaryReport').then((module) => module.InjurySummaryReport));
const MonthlyPhysiotherapyReport = lazyComponent(() => import('./reports/MonthlyPhysiotherapyReport').then((module) => module.MonthlyPhysiotherapyReport));
const PlayerMedicalFormReport = lazyComponent(() => import('./reports/PlayerMedicalFormReport').then((module) => module.PlayerMedicalFormReport));

/**
 * Physiotherapy: injuries, assessments, treatments, clinical notes, rehabilitation, return to play, documents and the reports that go
 * with them. It stands on Athletes and Base. It adds its tabs, cards and buttons to the athlete's page and the dashboard through the
 * hooks those screens offer, so with this module uninstalled nothing of it remains anywhere. Every route and tab requires an explicit
 * clinical permission (deny by default).
 */
export const physioModule: PlatformModule = {
  id: 'physio',
  nav: [
    {
      id: 'physio',
      label: 'Physio',
      to: '/physio',
      icon: HeartPulse,
      permission: 'injury.view',
      section: 'module',
      order: 40,
      children: [
        { id: 'physio-injuries', label: 'Injuries', to: '/physio/injuries', icon: Bandage, permission: 'injury.view', section: 'module', order: 2 },
        { id: 'physio-treatments', label: 'Treatments', to: '/physio/treatments', icon: HandHeart, permission: 'treatment.view', section: 'module', order: 3 },
        { id: 'physio-rehab', label: 'Rehabilitation', to: '/physio/rehabilitation', icon: Dumbbell, permission: 'rehab.view', section: 'module', order: 4 },
        { id: 'physio-exercises', label: 'Exercises', to: '/physio/exercises', icon: Dumbbell, permission: 'injury.view', section: 'module', order: 5 },
        { id: 'physio-rtp', label: 'Return to Play', to: '/physio/return-to-play', icon: Flag, permission: 'rtp.view', section: 'module', order: 6 },
        { id: 'physio-medical-forms', label: 'Medical Forms', to: '/physio/medical-forms', icon: ClipboardPlus, permission: 'medical_profile.view', section: 'module', order: 7 },
        { id: 'physio-monthly-report', label: 'Monthly injury report', to: '/reports/monthly-injury-report', icon: FileSpreadsheet, permission: 'injury.view', section: 'module', order: 8 },
        { id: 'physio-config', label: 'Configuration', to: '/physio/configuration', icon: Stethoscope, permission: 'injury.view', section: 'module', order: 9 },
      ],
    },
  ],
  routes: physioRoutes,
  routeExtensions: { athlete: physioAthleteRoutes },
  // The Dashboard entry shows whoever holds clinical access their physiotherapy dashboard, in place of the squad dashboard.
  home: { order: 10, permission: 'injury.view', component: PhysioDashboard },
  athleteTabs: [
    { id: 'injuries', label: 'Injuries', path: 'injuries', permission: 'injury.view', order: 30, icon: Bandage },
    { id: 'treatments', label: 'Treatments', path: 'treatments', permission: 'treatment.view', order: 40, icon: HandHeart },
    { id: 'rehabilitation', label: 'Rehabilitation', path: 'rehabilitation', permission: 'rehab.view', order: 50, icon: Activity },
    { id: 'documents', label: 'Documents', path: 'documents', permission: 'document.view', order: 60, icon: ClipboardList },
    { id: 'medical-profile', label: 'Medical forms', path: 'medical-profile', permission: 'medical_profile.view', order: 70, icon: HeartPulse },
  ],
  searchKinds: [{ kind: 'injury', heading: 'Injuries', icon: Bandage }],
  quickActions: [
    { id: 'new-injury', label: 'Injury', icon: Bandage, to: '/physio/injuries/new', permission: 'injury.create', order: 20 },
    { id: 'new-treatment', label: 'Treatment', icon: HandHeart, to: '/physio/treatments?new=1', permission: 'treatment.create', order: 30 },
    { id: 'new-rehab', label: 'Rehab plan', icon: Plus, to: '/physio/rehabilitation?new=1', permission: 'rehab.manage', order: 50 },
    { id: 'new-medical-form', label: 'Medical form', icon: ClipboardPlus, to: '/physio/medical-forms/new', permission: 'medical_profile.edit', order: 60 },
  ],
  reports: [
    {
      id: 'monthly-injury-report',
      slug: 'monthly-injury-report',
      title: 'Monthly injury report',
      description: 'A school-branded, detailed physiotherapy register for a selected date range, downloaded directly as Excel.',
      icon: FileSpreadsheet,
      permission: 'injury.view',
      clinical: true,
      order: 10,
      component: MonthlyPhysiotherapyReport,
    },
    {
      id: 'injury-summary',
      slug: 'injury-summary',
      title: 'Injury summary',
      description: 'Injury counts by body area, mechanism, training versus match, and status.',
      icon: FileBarChart,
      permission: 'injury.view',
      clinical: true,
      order: 20,
      component: InjurySummaryReport,
    },
    {
      id: 'injury-history',
      slug: 'injury-history',
      title: 'Athlete injury history',
      description: 'One athlete’s injuries, dates, status, treatment summary and days unavailable.',
      icon: FileBarChart,
      permission: 'injury.view',
      clinical: true,
      order: 30,
      component: InjuryHistoryReport,
    },
    {
      id: 'clinical-case-report',
      slug: 'clinical-case-report',
      title: 'Clinical case report',
      description: 'Every open or recently closed case in one table — diagnosis, management, rehab, gym/S&C and return-to-play, for any date range.',
      icon: FileBarChart,
      permission: 'injury.view',
      clinical: true,
      order: 40,
      component: ClinicalCaseReport,
    },
    {
      id: 'player-medical-form',
      slug: 'player-medical-form',
      title: 'Player medical form',
      description: "Player Medical Form A: profile, insurance, immunization, vitals and concussion history from an athlete's latest form.",
      icon: HeartPulse,
      permission: 'medical_profile.view',
      clinical: true,
      order: 50,
      component: PlayerMedicalFormReport,
    },
  ],
  access: {
    title: 'Physiotherapy',
    order: 30,
    levels: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Read injuries, investigations, diagnoses, treatments, rehabilitation, medical management, return to play, documents, earlier notes and assessments, and medical forms.',
        perms: [
          'injury.view',
          'investigation.view',
          'diagnosis.view',
          'medical_management.view',
          'clinical_note.view',
          'assessment.view',
          'treatment.view',
          'rehab.view',
          'rtp.view',
          'document.view',
          'medical_profile.view',
        ],
      },
      {
        id: 'user',
        label: 'User',
        description: 'Also record injuries, investigations, diagnoses, treatments and medical management, run rehabilitation and return to play, upload documents, record medical forms and change availability.',
        perms: [
          'injury.create',
          'investigation.manage',
          'diagnosis.manage',
          'medical_management.manage',
          'clinical_note.create',
          'assessment.create',
          'treatment.create',
          'rehab.manage',
          'rtp.manage',
          'document.upload',
          'medical_profile.edit',
          'availability.update',
        ],
      },
      { id: 'administrator', label: 'Administrator', description: 'Also correct injury records.', perms: ['injury.edit'] },
    ],
    extras: [{ id: 'configuration', label: 'Configuration', description: 'Change the lists, the injury pathway and the assessment templates.', perms: ['physio_config.manage'] }],
    permissions: {
      'injury.view': 'See injuries',
      'investigation.view': 'See investigations (scans, specialist reports)',
      'diagnosis.view': "See the physio's and medical diagnoses",
      'medical_management.view': 'See medical management (prescriptions, injections)',
      'clinical_note.view': 'Read clinical notes',
      'assessment.view': 'See assessments',
      'treatment.view': 'See treatments',
      'rehab.view': 'See rehabilitation plans',
      'rtp.view': 'See return-to-play assessments and decisions',
      'document.view': 'See clinical documents',
      'medical_profile.view': 'See medical forms',
      'injury.create': 'Record injuries',
      'clinical_note.create': 'Write clinical notes',
      'assessment.create': 'Complete assessments',
      'treatment.create': 'Record treatments and support (taping, braces, walking aids)',
      'investigation.manage': 'Record and correct investigations',
      'diagnosis.manage': 'Record and revise diagnoses',
      'medical_management.manage': 'Record and correct medical management',
      'rehab.manage': 'Create and run rehabilitation plans, and add exercises',
      'rtp.manage': 'Assess return to play and decide on it',
      'document.upload': 'Upload clinical documents',
      'medical_profile.edit': 'Record, correct and void medical forms',
      'availability.update': "Change an athlete's availability",
      'injury.edit': 'Correct injury records',
      'physio_config.manage': 'Change the Physiotherapy lists, pathway and assessment templates',
    },
  },
  slots: {
    'athlete.actions': [{ id: 'record-injury', order: 10, permission: 'injury.create', component: RecordInjuryAction }],
    'athlete.overview.side': [{ id: 'injury-history', order: 10, permission: 'injury.view', component: AthleteInjuryHistory }],
    'athlete.overview.main': [{ id: 'clinical-cards', order: 10, permission: 'injury.view', component: AthleteClinicalCards }],
    // No permission: the notice decides itself, because people without clinical access are told why they see no clinical cards.
    'athlete.overview.notice': [{ id: 'clinical-notice', order: 10, component: AthleteClinicalNotice }],
    // The athletes list shows physios where each athlete's injury stands. (The kanban of athlete cards is Athletes' own and says nothing about injuries.)
    'athletes.list.column': [{ id: 'injury', order: 10, label: 'Injury', permission: 'injury.view', component: AthleteListInjuryColumn }],
  },
};
